// Role gate from /me: renders children when the user holds the level (Reader, Confidential, Operator, Admin), otherwise the 403 state, which pages also show on their own.
import { LockIcon } from "lucide-react";
import type { ReactNode } from "react";

import { ROLE_FOR_LEVEL, type AccessLevel } from "@/app/nav";
import { ROLE_LABELS } from "@/app/roles";
import { useCanAccess } from "@/app/session";
import { EmptyState } from "@/components/common/EmptyState";

interface RequireRoleProps {
  level: AccessLevel;
  children: ReactNode;
  fallback?: ReactNode;
  description?: string;
}

export function RequireRole({ level, children, fallback, description }: RequireRoleProps) {
  const allowed = useCanAccess(level);
  if (allowed) {
    return children;
  }
  if (fallback !== undefined) {
    return fallback;
  }
  return <RoleRequired level={level} description={description} />;
}

export function RoleRequired({
  level,
  description,
}: {
  level: AccessLevel;
  description?: string | undefined;
}) {
  const role = ROLE_LABELS[ROLE_FOR_LEVEL[level]];
  return (
    <EmptyState
      icon={LockIcon}
      title={`You need the ${role} role`}
      description={description ?? `Ask an administrator to grant the ${role} role to see this.`}
    />
  );
}
